import React, { useState, useEffect } from "react";
import {
  Text,
  Group,
  ActionIcon,
  UnstyledButton,
  Select,
  Textarea,
  Input,
  Autocomplete,
  TextInput,
} from "@mantine/core";
import RemoteSelect2 from "./remoteSelect2";
import DataTable from "./dataTable";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import _ from "lodash";
import DebitNoteAddEndorsementButton from "./debitNoteAddEndorsementButton";
import ActionCellRender from "./actionCellRender";
import { IconRefresh } from "@tabler/icons";
import moment from "moment";
import { useAuthUser } from "react-auth-kit";
import { useLocation } from "react-router-dom";
import { useCellRender } from "../hooks/useCellRender";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { openEntityEditTab } from "../utils/openEntityEditTab";
import { resolveEntityPath } from "../utils/resolveEntityPath";
import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "./pageList";
import { useFormRender } from "../hooks/useFormRender";
import FileList from "./fileList";
import { DatePicker } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { IconExclamationMark } from "@tabler/icons";
import {
  DATA_ENDORSEMENT_ACTION,
  DATA_ENDORSEMENT_TYPE,
} from "./debitNoteAddEndorsementButton";
import DummyWidget from "./dummyWidget";

const DebitNoteEndorsementList = ({
  form: mainForm,
  name,
  readonly = false,
}) => {
  const dnId = mainForm?.values?._id;
  const [rows, setRows] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [api] = useServerApi();
  const { t } = useTranslation();
  const auth = useAuthUser();
  const [cellRender] = useCellRender();
  const userRole = auth().userRole;
  const tabNav = useTabNavigate();
  const location = useLocation();

  const isModifiedClick = (event) =>
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey ||
    event.altKey ||
    event.button === 1;

  const openDocument = (e, { entity, id, label }) => {
    if (!id) return;
    if (isModifiedClick(e)) {
      const pathname = resolveEntityPath(
        entity,
        userRole,
        location.pathname,
      );
      if (pathname) {
        window.open(`${pathname}?id=${id}&mode=edit`, "_blank");
      }
      return;
    }
    e.preventDefault();
    openEntityEditTab(tabNav, {
      entity,
      id,
      label,
      userRole,
      currentPathname: location.pathname,
    });
  };

  const renderDocumentLink = (content, { entity, id, label }) => {
    if (!id) return content;
    return (
      <UnstyledButton
        onClick={(e) => openDocument(e, { entity, id, label })}
        style={{ cursor: "pointer", textAlign: "left" }}
      >
        {content}
      </UnstyledButton>
    );
  };

  const fetchEndorsements = async () => {
    try {
      if (!dnId) return;
      const data = await api.DebitNote.getEndorsements({
        id: dnId,
      });
      if (!data || !data.endorsements) return;
      // console.log(data);
      setRows(data.endorsements);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchEndorsements();
  }, [dnId]);

  const onAdded = () => {
    fetchEndorsements();
  };

  const deleteEndorsement = async (data) => {
    if (!window.confirm("Are you sure you want to delete this endorsement?"))
      return;

    const result = await api.DebitNote.deleteEndorsement({
      id: data._id,
    });
    // console.log("result", result);
    fetchEndorsements();
  };

  const updateEndorsement = async (values) => {
    console.log("updateEndorsement", values);
    try {
      if (!values._id) return;
      await api.updateById({
        apiEntity: "endorsement",
        id: values._id,
        values,
      });
      fetchEndorsements();
    } catch (error) {
      console.log(error);
    }
  };

  const handleActionBtnClick = ({ action, data, rowIndex }) => {
    // console.log("handleActionBtnClick", action, data, rowIndex);

    switch (action) {
      case "edit":
        // console.log("edit", data, rowIndex);
        formAction.open({ mode: "edit", id: data._id });
        break;
      case "delete":
        return deleteEndorsement(data);
    }
  };

  const tableSchema = {
    hidePagination: true,
    hideSearchBar: true,
    columns: [
      {
        field: "date",
        headerName: "Date",
        cellRender: (_, data) => (
          <>
            <div>
              <Text size="xs" c="dimmed">
                {data.code}
              </Text>
            </div>
            <div>{data.date ? moment(data.date).format("YYYY-MM-DD") : ""}</div>
          </>
        ),
      },

      { field: "endorsementType", headerName: "Endorsement Type" },
      {
        field: "action",
        headerName: "Action",
        cellRender: (_, data) => cellRender.EndorsementAction(data),
      },
      {
        field: "insurerEndorsementNo",
        headerName: "Insurer En No.",
      },
      {
        field: "document",
        headerName: "Document",
        cellRender: (_, data) => (
          <>
            {data.endorsedDebitNote
              ? renderDocumentLink(
                  cellRender.DebitNoteCode(data.endorsedDebitNote, false),
                  {
                    entity: "debitNote",
                    id: data.endorsedDebitNote._id,
                    label: data.endorsedDebitNote.code,
                  },
                )
              : ""}
            {data.endorsedCreditNote
              ? renderDocumentLink(
                  cellRender.CreditNote.code(data.endorsedCreditNote, false),
                  {
                    entity: "creditNote",
                    id: data.endorsedCreditNote._id,
                    label: data.endorsedCreditNote.code,
                  },
                )
              : ""}
          </>
        ),
      },
      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [],
        actionMenu: [
          {
            name: "edit",
            label: "Edit",
            props: {
              size: "xs",
            },
          },
          {
            name: "delete",
            label: "Delete",
            props: {
              color: "red",
              size: "xs",
            },
          },
        ],
      },
    ],
  };

  const formSchema = {
    name: "EditEndorsementSchema",
    title: "Edit Endorsement",
    description: "Please fill in the form to edit the Endorsement",
    closeAfterSave: true,

    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "1000px", //xs, sm, xl  ....100%
      position: "right",
    },

    apiEntity: "endorsement",
    initialValues: {
      type: "",
    },

    validate: {
      action: (value) =>
        !value ? "Please select the endorsement action" : null,
      type: (value) => (!value ? "Please select the endorsement type" : null),
      error: (value) => {
        // console.log("error", value);
        return value;
      },
    },
    showSaveBar: true,

    layout: {
      containers: [
        {
          key: "tabs",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "default", //default | pills | outline
            defaultValue: "Basic Info",
          },
          tab: [
            {
              key: "basicInfo",
              label: "Basic Info",
              value: "basicInfo",
            },
            {
              key: "files",
              label: "Files",
              value: "files",
            },
          ],
        },
        {
          key: "basicInfo",
          parent: "tabs",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,

          props: {},
        },
        {
          key: "files",
          parent: "tabs",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,

          props: {},
        },
      ],

      fields: [
        {
          name: "type",
          component: Autocomplete,
          parent: "basicInfo",
          props: {
            label: "Endorsement Type",
            data: DATA_ENDORSEMENT_TYPE,
            clearable: true,
            defaultValue: "",
          },
        },
        {
          name: "action",
          component: Select,
          parent: "basicInfo",
          props: {
            label: "Action",
            required: true,

            data: DATA_ENDORSEMENT_ACTION,
            clearable: true,
          },
        },
        {
          name: "date",
          parent: "basicInfo",
          component: DatePicker,
          type: "datePicker",

          props: {
            label: "Endorse Date",
            required: true,
            clearable: true,
            valueFormat: "YYYY-MM-DD",
            allowFreeInput: true,
          },
        },
        {
          name: "originalDebitNote",
          component: RemoteSelect2,
          parent: "basicInfo",
          props: {
            label: "Original DN",
            apiEntity: "debitNote",
          },
          props: {
            label: "Original DN",
            apiEntity: "debitNote",
            valueField: "_id",
            labelField: "code",
          },
        },

        {
          name: "insurerEndorsementNo",
          parent: "basicInfo",
          component: TextInput,
          props: {
            label: "Insurer Endorsement Number",
          },
        },

        {
          name: "remark",
          parent: "basicInfo",
          component: Textarea,
          props: {
            label: "Remark",
          },
        },

        {
          name: "files",
          parent: "files",
          component: FileList,
          props: {},
        },
      ],
    },
  };

  const onSubmit = async ({ values, formMode }) => {
    try {
      // console.log("DebitNoteAddEndorsementButton  > onSubmit", values);
      await updateEndorsement(values);

      formAction.close();
      showNotification({
        title: `Service added`,
        message: "Service added succesfully",
      });
    } catch (error) {
      console.log(error);
      showNotification({
        color: "red",
        icon: <IconExclamationMark size={18} />,
        title: `Service add error`,
        message: error.msg ?? error.message ?? error,
      });
    }
  };

  const [renderForm, formAction, formStatus, formSetting] = useFormRender(
    formSchema,
    null,
    onSubmit
  );

  return (
    <>
      <Group position="right" mb="xs">
        <UnstyledButton onClick={() => fetchEndorsements()}>
          <Group spacing={2}>
            <ActionIcon size="sm">
              <IconRefresh size={16} />
            </ActionIcon>
            <Text size={"xs"}>{t("Refresh")}</Text>
          </Group>
        </UnstyledButton>
      </Group>
      {_.isEmpty(rows) && (
        <Group mb="xl">
          <Text size="xs">{t("No endorsements records found")}</Text>
        </Group>
      )}
      {!_.isEmpty(rows) && (
        <DataTable
          data={rows ?? []}
          columns={tableSchema.columns}
          hidePagination={true}
          hideSearchBar={true}
          // onRowDoubleClick={handleOnEdit}
          onActionBtnClick={handleActionBtnClick}
        />
      )}

      {!printing && !readonly && (
        <DebitNoteAddEndorsementButton mainForm={mainForm} onAdded={onAdded} />
      )}
      {renderForm()}
      {/* {renderChangeMasterForm()} */}
    </>
  );
};

export default DebitNoteEndorsementList;
